import { GoogleGenAI, Type } from "@google/genai";
import { BibleVersion } from '../types';

const getAiClient = () => {
  // Ensure API Key is present
  const apiKey = process.env.API_KEY;
  if (!apiKey) {
      console.error("API_KEY is missing in environment variables.");
      throw new Error("API Key is missing. Please check your configuration.");
  }
  return new GoogleGenAI({ apiKey });
};

const LANGUAGE_MAP: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'pt': 'Portuguese',
    'fr': 'French'
};

export const checkContentSafety = async (text: string): Promise<{ safe: boolean; reason?: string }> => {
  try {
    const ai = getAiClient();
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: `Analyze the following text for profanity, obscenity, hate speech, sexual content, or non-Christian friendly language (like excessive swearing). 
      Text: "${text}"
      
      Return JSON with 'safe' (boolean) and 'reason' (string, optional).`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            safe: { type: Type.BOOLEAN },
            reason: { type: Type.STRING }
          },
          required: ["safe"]
        }
      }
    });

    if (response.text) {
        return JSON.parse(response.text);
    }
    return { safe: true };

  } catch (error) {
    console.error("Gemini safety check failed:", error);
    // Allow content if check fails to prevent blocking user due to API errors, but log it.
    return { safe: true }; 
  }
};

export const checkMediaSafety = async (base64Data: string, mimeType: string): Promise<{ safe: boolean; reason?: string }> => {
    try {
        const ai = getAiClient();
        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: {
                parts: [
                    { inlineData: { mimeType, data: base64Data } },
                    { text: "Analyze this media. Is it safe for a general audience? Return JSON { safe: boolean, reason: string }." }
                ]
            },
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        safe: { type: Type.BOOLEAN },
                        reason: { type: Type.STRING }
                    },
                    required: ["safe"]
                }
            }
        });

        if (response.text) {
            return JSON.parse(response.text);
        }
        return { safe: true };
    } catch (error) {
        console.error("Media safety check failed:", error);
        return { safe: true };
    }
};

export const getDailyVerse = async (language: string = 'en'): Promise<{ reference: string; text: string; imageKeyword: string }> => {
  try {
    const langName = LANGUAGE_MAP[language] || 'English';
    const ai = getAiClient();
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: `Give me an encouraging daily bible verse. The text and reference MUST be in ${langName}. Return JSON with 'reference', 'text', and a single visual 'imageKeyword' (keyword must be in English for image generation).`,
      config: {
        responseMimeType: "application/json",
         responseSchema: {
          type: Type.OBJECT,
          properties: {
            reference: { type: Type.STRING },
            text: { type: Type.STRING },
            imageKeyword: { type: Type.STRING }
          }
        }
      }
    });
    
    if (response.text) {
        return JSON.parse(response.text);
    }
    throw new Error("No text returned");
  } catch (error) {
    console.error("Daily verse failed:", error);
    return {
      reference: "Psalm 23:1",
      text: "The Lord is my shepherd; I shall not want.",
      imageKeyword: "shepherd"
    };
  }
};

export const suggestBiblePassage = async (query: string, version: BibleVersion, language: string = 'en'): Promise<{ reference: string; text: string }> => {
    try {
        const langName = LANGUAGE_MAP[language] || 'English';
        const ai = getAiClient();
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: `Find the most relevant bible verse for: "${query}". If the target language is English, use ${version} version. If not, use a standard popular version in ${langName}. The output text and reference MUST be in ${langName}. Return JSON { reference, text }.`,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        reference: { type: Type.STRING },
                        text: { type: Type.STRING }
                    }
                }
            }
        });
        if(response.text) return JSON.parse(response.text);
        throw new Error("Empty response from AI");
    } catch (e: any) {
        console.error("Verse suggestion failed:", e);
        // Return the actual error message to display in the UI
        return { 
            reference: "Error", 
            text: e.message?.includes("API Key") ? "API Key missing or invalid." : (e.message || "Could not fetch verse.") 
        };
    }
};

export const searchPlaces = async (query: string): Promise<string[]> => {
    try {
        const ai = getAiClient();
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: `List 5 real cities matching: "${query}". Return JSON array of strings "City, Country".`,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.ARRAY,
                    items: { type: Type.STRING }
                }
            }
        });
        if(response.text) return JSON.parse(response.text);
        return [];
    } catch (e) {
        console.error("Place search failed", e);
        return [];
    }
};

export const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
        const ai = getAiClient();
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: `City and Country for Lat ${lat}, Lng ${lng}? Return string.`,
        });
        return response.text?.trim() || "Unknown Location";
    } catch (e) {
        console.error("Reverse geocode failed", e);
        return "Location unavailable";
    }
};